import { addonActions } from '@buy-viasat/redux/src/addons';
import {
  closingOffersActions,
  selectSkipClosingOffer,
  selectUpsertWithClosingOffersError,
} from '@buy-viasat/redux/src/closingOffers';
import { AddressType, Coordinates } from '@buy-viasat/redux/src/address';
import { DataLayerEvents, TrackingActions, TrackingCategory } from '@buy-viasat/redux/src/analytics';
import {
  appActions,
  Modals,
  QueryParams,
  selectCustomerType,
  selectErrorModalAttempts,
  selectSalesAgreementId,
  CustomerType,
  selectUrlParams,
  selectCountry,
  Country,
} from '@buy-viasat/redux/src/app';
import { navActions, Routes } from '@buy-viasat/redux/src/navigator';
import { scheduleInstallationActions } from '@buy-viasat/redux/src/schedule-installation';
import {
  planActions,
  ProductSegment,
  sanitizeAddressLines,
  selectAvailableProducts,
  selectPlanId,
  selectPlanOfferId,
  SetPlanAction,
} from '@buy-viasat/redux/src/plan';
import {
  selectCoordinates,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import {
  ClosingOffer,
  GetAddOnsInput,
  GetClosingOfferInput,
  LocationInput,
  ShoppingCartPlanInput,
  FeatureFlags,
  Product,
  ShoppingCart,
} from '@buy-viasat/types/build/bv';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  all,
  AllEffect,
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeLatest,
} from 'redux-saga/effects';
import { benchmark } from 'shared/containers/App/saga';
import upsertCartPlanAsync, { UpsertCartPlanResponse } from '../../providers/requests/cart/upsertCartPlan';
import { upsertCartPlanRest } from '../../providers/requests/cart/upsertCartPlanRest';
import getAddonsAsync, { GetAddOnsPayload } from '../../providers/requests/products/getAddOns';
import getPlansAsync from '../../providers/requests/products/getPlans';
import clientLogger from '../../utils/clientLogger';
import { generateCart } from '../Cart/saga';
import { selectIsPersonalInformationRequestComplete } from '@buy-viasat/redux/src/personal-information';
import { cartActions } from '@buy-viasat/redux/src/cart/slice';
import getClosingOfferAsync from '../../providers/requests/products/getClosingOffers';
import { adobeTargetDataLayer, isDefined, ProductFamily } from '@buy-viasat/utils';
import { consentActions } from 'shared/containers/disclosures/redux/slice';

import { fiberWaitListActions, selectWaitlistInformation } from '@buy-viasat/redux/src/fiber-waitlist';
import { FiberErrors } from 'shared/types';
import applyForFiberWaitlistAsync from '../../providers/requests/fiber/applyForFiberWaitlist';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import { getClosingOfferRest } from 'shared/providers/requests/products/getClosingOfferRest';

/**
 *  Getting Available Products request/response handler
 *
 *  Generator typing (type of effects we yield, generator's return type, types yielded)
 *
 */
export function* getAvailableProductsSaga() {
  const { dealerId, partnerId } = yield select(selectUrlParams);
  const serviceAddress: AddressType = yield select(selectServiceAddressValues);
  const coordinates: Coordinates = yield select(selectCoordinates);
  const location: LocationInput = { address: serviceAddress };
  if ([coordinates?.latitude, coordinates?.longitude].every(isDefined))
    location.coordinates = { latitude: coordinates.latitude, longitude: coordinates.longitude };
  const salesAgreementId: string = yield select(selectSalesAgreementId);
  const customerType: CustomerType = yield select(selectCustomerType);
  const country: Country = yield select(selectCountry);
  try {
    const { data } = yield call(getPlansAsync, {
      location,
      dealerId,
      partnerId,
      salesAgreementId,
      customerType,
      country,
    });
    if (data.getAvailableProducts.length) {
      yield put(
        appActions.analyticsEvent({
          category: TrackingCategory.PLANS,
          action: TrackingActions.SUCCESSFUL,
          params: undefined,
        }),
      );
      yield put(planActions.setAvailableProducts(data.getAvailableProducts));
    } else {
      yield put(navActions.previous());
      yield put(planActions.setIsPlanLoading(false));
      yield put(appActions.setModalVisible(Modals.NO_AVAILABLE_PLANS));
    }
  } catch (err: any) {
    yield call(clientLogger, 'getAvailableProductsSaga: ', err);
    yield put(navActions.previous());
    yield put(planActions.setIsPlanLoading(false));
    yield put(appActions.setModalVisible(Modals.GET_PLANS_SERVER_ERROR));
  }
}

export function* getAvailableClosingOfferSaga() {
  const { offerId: closingOfferIdURLParam } = yield select(selectUrlParams);
  const serviceAddress: AddressType = yield select(selectServiceAddressValues);
  const coordinates: Coordinates = yield select(selectCoordinates);
  const salesAgreementId: string = yield select(selectSalesAgreementId);
  const planOfferId: string = yield select(selectPlanOfferId);
  const planId: string = yield select(selectPlanId);
  const country: string = yield select(selectCountry);

  const getClosingOfferInput: GetClosingOfferInput = {
    location: { address: serviceAddress, coordinates },
    offerId: planOfferId,
    rootProductId: planId,
    salesAgreementId,
    productIds: [closingOfferIdURLParam],
    country,
  };
  let closingOfferResponse: ClosingOffer | undefined;
  try {
    if (USE_NEW_API) {
      closingOfferResponse = yield call(getClosingOfferRest, getClosingOfferInput);
    } else {
      const { data } = yield call(getClosingOfferAsync, getClosingOfferInput);
      closingOfferResponse = data?.getClosingOffer;
    }
    if (closingOfferResponse?.id) {
      adobeTargetDataLayer.push({
        event: DataLayerEvents.AA_EVENT,
        eventData: {
          event: 'closingOffersView',
          closingOffers: {
            id: closingOfferResponse.id,
            name: closingOfferResponse.name,
          },
        },
      });

      yield put(closingOffersActions.setAvailableClosingOffers([closingOfferResponse]));
      return closingOfferResponse;
    } else {
      yield put(appActions.setModalVisible(Modals.CLOSING_OFFER_NOT_ELIGIBLE));
    }
  } catch (err: any) {
    yield put(appActions.setModalVisible(Modals.CLOSING_OFFER_NOT_ELIGIBLE));
    yield call(clientLogger, 'getAvailableClosingOffers: ', err);
  }
}

export function* planOnNextSaga(
  action: PayloadAction<SetPlanAction>,
): Generator<
  PutEffect | CallEffect | SelectEffect | AllEffect<CallEffect>,
  void,
  QueryParams &
    [UpsertCartPlanResponse, GetAddOnsPayload] &
    AddressType &
    Coordinates &
    FeatureFlags &
    void &
    CustomerType &
    string
> {
  const serviceAddress: AddressType = yield select(selectServiceAddressValues);
  const coordinates: Coordinates = yield select(selectCoordinates);
  const salesAgreementId = yield select(selectSalesAgreementId);
  const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  const { dealerId, partnerId, offerId: closingOfferIdURLParam } = yield select(selectUrlParams);

  try {
    const customerType: CustomerType = yield select(selectCustomerType);
    const country: string = yield select(selectCountry);
    const isUpsertWithClosingOffersError: boolean = yield select(selectUpsertWithClosingOffersError);
    const attempts = yield select(selectErrorModalAttempts);

    const skipClosingOffer: boolean = yield select(selectSkipClosingOffer);

    const HAS_REACHED_MAX_RETRY_ATTEMPTS = attempts >= 3;

    yield put(navActions.invalidateProgressFrom(Routes.PLAN));
    yield put(
      cartActions.setCartItems({
        plan: {},
        addons: [],
      }),
    );

    const getAddOnsInput: GetAddOnsInput = {
      // Match getAvailableProducts: send the exact addressLines scrubAddress returned.
      location: sanitizeAddressLines(
        { address: serviceAddress, coordinates } as LocationInput,
        unsanitizedAddressLines,
      ),
      rootProductId: action.payload.id,
      dealerId,
      partnerId,
      offerId: action.payload.offerId,
      salesAgreementId,
      country,
    };

    const inputForCart: ShoppingCartPlanInput = {
      planProductId: action.payload.id,
      cartId: action.payload.offerId,
      customerType,
      country,
    };

    yield put(planActions.setIsUpsertPlanLoading(true));
    yield put(planActions.setHavePlansLoaded(false));

    customerType === CustomerType.B
      ? (getAddOnsInput.productSegment = ProductSegment.BUSINESS)
      : (getAddOnsInput.productSegment = ProductSegment.RESIDENTIAL);

    if (!action.payload.retryNeeded && !isUpsertWithClosingOffersError && !skipClosingOffer) {
      yield put(navActions.next());
    }

    yield put(cartActions.setCartId(action.payload.offerId));

    yield put(
      planActions.setPlanId({
        id: action.payload.id,
        extensionProducts: action.payload.extensionProducts,
        name: action.payload.name,
        planPrice: action.payload.planPrice,
        planPromo: action.payload.planPromo,
        inflectionPointText: action.payload.inflectionPointText,
        offerId: action.payload.offerId,
        productFamily: action.payload.productFamily,
        status: action.payload.status,
        isRegulated: action.payload.isRegulated,
        isCafII: action.payload.isCafII,
        dataCap: action.payload.dataCap,
        customerType: action.payload.customerType,
        country: action.payload.country,
      }),
    );

    // TODO - migrate this saga-driven REST call to the tanstack-query hook pattern in a follow-up
    const planCall = USE_NEW_API
      ? call(upsertCartPlanRest, inputForCart)
      : call(upsertCartPlanAsync, { input: inputForCart });

    const planCalls = [planCall, call(getAddonsAsync, getAddOnsInput)];

    if (closingOfferIdURLParam && !HAS_REACHED_MAX_RETRY_ATTEMPTS && !skipClosingOffer) {
      const closingOfferResponse: ClosingOffer = yield call<any>(getAvailableClosingOfferSaga);

      if (closingOfferResponse) {
        if (closingOfferIdURLParam === closingOfferResponse.id) {
          inputForCart.closingOfferProductId = closingOfferResponse.id;
          yield put(planActions.setValidatedClosingOfferId(closingOfferResponse.id));
        }
      } else {
        console.error('No closing offers retrieved');
      }
    }

    const result: [UpsertCartPlanResponse | ShoppingCart, GetAddOnsPayload] = yield all(planCalls);

    yield put(planActions.setIsUpsertPlanLoading(false));

    const [cart, addonsResult] = result;

    const shoppingCart: ShoppingCart = USE_NEW_API
      ? (cart as ShoppingCart)
      : ((cart as UpsertCartPlanResponse).data?.upsertCartPlan as ShoppingCart);

    const cartProducts = [shoppingCart?.cartItems?.plan];

    yield put(consentActions.setCartProducts({ cartProducts }));

    const bundledAddons = shoppingCart?.bundledAddons ?? [];
    const addOns = addonsResult?.data?.getAddOns?.addOns ?? [];
    const addOnGroup = addonsResult?.data?.getAddOns?.addOnGroup;
    const addonsList = [...addOns, ...bundledAddons];

    if (!shoppingCart) {
      yield put(planActions.setIsUpsertPlanLoading(false));
      throw new Error('Upsert cart failed');
    }

    yield call(generateCart, shoppingCart);
    yield put(scheduleInstallationActions.getInstallationDates(new Date().toString()));
    yield put(addonActions.setAvailableAddons({ addOns: addonsList, addOnGroup }));
    yield put(planActions.setIsPlanLoading(false));

    const isPersonalInformationRequestComplete = yield select(selectIsPersonalInformationRequestComplete);

    if (isPersonalInformationRequestComplete) {
      yield put(navActions.next());
    }
  } catch (err: any) {
    if (closingOfferIdURLParam) {
      yield put(closingOffersActions.setUpsertWithClosingOffersError(true));
      yield put(
        appActions.setErrorRetryAction({
          type: planActions.planOnNext.type,
          payload: { ...action.payload, retryNeeded: true },
        }),
      );
      yield put(closingOffersActions.setSkipClosingOffer(false));
      yield put(appActions.setModalVisible(Modals.UPSERT_WITH_CLOSING_OFFER_RETRY_ERROR));
    } else {
      clientLogger('planOnNextSaga Error: ', err);
      yield put(
        appActions.setErrorRetryAction({
          type: planActions.planOnNext.type,
          payload: { ...action.payload, retryNeeded: true },
        }),
      );
      yield put(appActions.setModalVisible(Modals.ERROR));
    }
    yield put(planActions.setIsUpsertPlanLoading(false));
    yield put(planActions.setIsPlanLoading(false));
  }
}

export function* applyForFiberWaitlistSaga() {
  const { firstName, lastName, email, phone: phoneNumber } = yield select(selectWaitlistInformation);
  const availableProducts: Product[] = yield select(selectAvailableProducts);
  const addressId = availableProducts.find((product) => product.characteristics?.productFamily === ProductFamily.FIBER)
    ?.characteristics.addressId;

  if (!addressId) {
    yield put(appActions.setModalVisible(Modals.ERROR));
    yield put(fiberWaitListActions.setIsWaitlistRequestComplete(false));
    throw new Error(FiberErrors.NO_ADDRESS_ID);
  }

  if (firstName && lastName && email) {
    const waitlistInput = {
      firstName,
      lastName,
      email,
      phoneNumber,
      addressId: addressId,
    };
    try {
      yield put(fiberWaitListActions.setIsWaitlistLoading(true));
      yield call(applyForFiberWaitlistAsync, { input: waitlistInput });
    } catch (error) {
      yield put(appActions.setModalVisible(Modals.ERROR));
      yield put(fiberWaitListActions.setIsWaitlistRequestComplete(false));

      yield put(
        appActions.setErrorRetryAction({ type: fiberWaitListActions.applyForFiberWaitlist.type, payload: null }),
      );
      return;
    } finally {
      yield put(fiberWaitListActions.setIsWaitlistLoading(false));
    }
    yield put(fiberWaitListActions.setIsWaitlistRequestComplete(true));
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export function* planSaga(): Generator<ForkEffect, void, void> {
  yield takeLatest(planActions.getPlans.type, benchmark(getAvailableProductsSaga));
  yield takeLatest(
    planActions.planOnNext.type,
    benchmark<
      SetPlanAction,
      PutEffect | CallEffect | SelectEffect | AllEffect<CallEffect>,
      void,
      [UpsertCartPlanResponse, GetAddOnsPayload] & AddressType & void
    >(planOnNextSaga),
  );
  yield takeLatest(planActions.getAvailableClosingOffers.type, benchmark(planOnNextSaga));
  yield takeLatest(fiberWaitListActions.applyForFiberWaitlist.type, benchmark(applyForFiberWaitlistSaga));
}
